import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Zap, Shield, Droplets, Flame, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import FlameStrikethrough from './icons/FlameStrikethrough';

const carouselSlides = {
  ru: [
    { image: '/images/gkl_director.webp' },
    { image: '/images/gkl_office.webp' },
    { image: '/images/gkl_elevator.webp' },
    { image: '/images/gkl_gym.webp' },
    { image: '/images/gkl_classroom.webp' }
  ],
  kz: [
    { image: '/images/gkl_director.webp' },
    { image: '/images/gkl_office.webp' },
    { image: '/images/gkl_elevator.webp' },
    { image: '/images/gkl_gym.webp' },
    { image: '/images/gkl_classroom.webp' }
  ],
  en: [
    { image: '/images/gkl_director.webp' },
    { image: '/images/gkl_office.webp' },
    { image: '/images/gkl_elevator.webp' },
    { image: '/images/gkl_gym.webp' },
    { image: '/images/gkl_classroom.webp' }
  ]
};

const InnovativeMaterial = () => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = carouselSlides[language] || carouselSlides.ru;

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const content = {
    ru: {
      title: 'Панели GIPSOVINIL.KZ - инновационный материал',
      description: 'Гипсовиниловые панели — это сочетание слабогорючей плиты и износостойкого, моющегося декоративного ПВХ покрытия.',
      features: [
        { icon: Zap, text: 'Ускоряет отделку в 5 раз' },
        { icon: 'FlameStrikethrough', text: 'Класс горючести Г1 (слабогорючие)' },
        { icon: Droplets, text: 'Влагостойкость — применимы в бассейнах' },
        { icon: CheckCircle2, text: 'Одобрено СЭС, моющееся покрытие' }
      ],
    },
    kz: {
      title: 'GIPSOVINIL.KZ панельдері - инновациялық материал',
      description: 'Гипсвинил панельдері — әлсіз жанатын плита мен тозуға төзімді ПВХ жабынның үйлесімі.',
      features: [
        { icon: Zap, text: 'Әрлеуді 5 есе жылдамдатады' },
        { icon: 'FlameStrikethrough', text: 'Жану класы Г1' },
        { icon: Droplets, text: 'Ылғалға төзімді' },
        { icon: CheckCircle2, text: 'СЭС мақұлдаған' }
      ],
    },
    en: {
      title: 'GIPSOVINIL.KZ Panels - Innovative Material',
      description: 'Gypsum-vinyl panels combine low-flammability board and wear-resistant, washable decorative PVC coating.',
      features: [
        { icon: Zap, text: 'Speeds up finishing 5 times' },
        { icon: 'FlameStrikethrough', text: 'Fire class G1 (low flammable)' },
        { icon: Droplets, text: 'Moisture resistant - suitable for pools' },
        { icon: CheckCircle2, text: 'SES approved, washable coating' }
      ],
    },
    ar: {
      title: 'ألواح GIPSOVINIL.KZ — مادة مبتكرة',
      description: 'تجمع الألواح الجبسية الفينيلية بين لوح منخفض الاشتعال وطبقة تزيينية من PVC مقاومة للبلى وقابلة للغسيل.',
      features: [
        { icon: Zap, text: 'تُسرِّع أعمال التشطيب 5 مرات' },
        { icon: 'FlameStrikethrough', text: 'فئة الحريق G1 (منخفض الاشتعال)' },
        { icon: Droplets, text: 'مقاوم للرطوبة — مناسب لأحواض السباحة' },
        { icon: CheckCircle2, text: 'معتمد صحياً، طبقة تشطيب قابلة للغسيل' }
      ],
    }
  };

  const t = content[language] || content.en || content.ru;

  return (
    <section className="pt-6 py-2 pb-6 bg-[#061d36]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-0">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{t.title}</h2>
          </div>
          
          <div className="rounded-3xl p-8 md:p-12 pt-4">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-1">
                <p className="text-lg md:text-2xl leading-relaxed mb-8 font-semibold text-white">{t.description}</p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {t.features.map((feature, idx) => {
                    const isFlameStrikethrough = feature.icon === 'FlameStrikethrough';
                    const Icon = isFlameStrikethrough ? null : feature.icon;
                    return (
                      <div key={idx} className="flex items-center gap-3 bg-[#061d36] rounded-xl p-4">
                        {isFlameStrikethrough ? (
                          <FlameStrikethrough className="w-6 h-6 text-blue-300 flex-shrink-0" />
                        ) : (
                          <Icon className="w-6 h-6 text-blue-300 flex-shrink-0" />
                        )}
                        <span className="text-sm text-white">{feature.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="w-full md:w-[480px] flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden border-2 border-white/10" data-testid="innovative-carousel">
                  <div className="relative h-80">
                    {slides.map((slide, idx) => (
                      <div
                        key={idx}
                        className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                      >
                        <img
                          src={slide.image}
                          alt={slide.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#061d36]/30 via-transparent to-transparent" />
                      </div>
                    ))}
                  </div>
                  <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors" aria-label="Previous">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors" aria-label="Next">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {slides.map((_, idx) => (
                      <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-white w-4' : 'bg-white/40'}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InnovativeMaterial;
